import { useMemo, useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { createStructuredSelector } from 'reselect'

import {
  useBundlesModalReturn,
  SelectBundlesSelectors,
  BundleType,
} from '../../model/bundles.model'
import { fetchBundles, setSelectedBundles } from '../../store/bundles.reducer'
import {
  selectBundlesList,
  selectSelectedBundlesIds,
  selectBundlesListError,
} from '../../store/bundles.selectors'

import { RootState } from '~/core/store/root.reducer'
import { BUNDLE_ERROR } from '~/modal/constants/messages'
import useModal from '~/modal/hooks/useModal'
import {
  selectBundlesAndTariffPrice,
  selectTariffPriceFormatted,
  selectTariffFinalPrice,
} from '~/prices/prices.selectors'
import { selectSelectedTariffId, selectTariffName } from '~/tariff/store/tariff.selectors'

export const selectBundlesSelectors = (id: string, type: BundleType) =>
  createStructuredSelector<RootState, SelectBundlesSelectors>({
    bundlesList: (state: RootState) => selectBundlesList(state, id, type),
    selectedBundles: (state: RootState) => selectSelectedBundlesIds(state, id, type),
    selectedTariffId: (state: RootState) => selectSelectedTariffId(state, id, type),
    tariffName: (state: RootState) => selectTariffName(state, id, type),
    bundlesAndTariffPrice: (state: RootState) => selectBundlesAndTariffPrice(state, id),
    formattedTariffPrice: (state: RootState) => selectTariffPriceFormatted(state, id, type),
    bundlesError: (state: RootState) => selectBundlesListError(state, id, type),
    tariffPrice: (state: RootState) => selectTariffFinalPrice(state, id, type),
  })

export default function useBundlesModal(lineId: string, type: BundleType): useBundlesModalReturn {
  const [isOpen, setIsOpen] = useState(false)
  const errorModal = useModal()

  const dispatch = useDispatch()

  const {
    bundlesList,
    selectedBundles,
    selectedTariffId,
    tariffName,
    bundlesAndTariffPrice,
    formattedTariffPrice,
    bundlesError,
    tariffPrice,
  } = useSelector((state: RootState) => selectBundlesSelectors(lineId, type)(state))

  const [preSelectedBundles, setPreselectedBundles] = useState(selectedBundles)

  const canAddBundle = useMemo(() => bundlesList.length !== selectedBundles.length, [
    bundlesList,
    selectedBundles,
  ])

  const isDisabled = useMemo(() => !bundlesList.length, [bundlesList])

  const cancelBundlesSet = useCallback(() => {
    dispatch(setSelectedBundles({ id: lineId, bundles: preSelectedBundles, type }))
  }, [preSelectedBundles])

  useEffect(() => {
    dispatch(setSelectedBundles({ id: lineId, bundles: [], type }))
    if (selectedTariffId) {
      dispatch(fetchBundles({ id: lineId, type, tariffId: selectedTariffId, tariffPrice }))
    }
  }, [selectedTariffId])

  useEffect(() => {
    if (isOpen) {
      setPreselectedBundles(selectedBundles)
    }
  }, [isOpen])

  useEffect(() => {
    errorModal.setModalConfig({
      open: false,
      title: BUNDLE_ERROR.title,
      description: BUNDLE_ERROR.description,
      actions: [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: BUNDLE_ERROR.actions.retry,
          fn: () => {
            if (selectedTariffId)
              dispatch(fetchBundles({ id: lineId, type, tariffId: selectedTariffId, tariffPrice }))
          },
        },
      ],
      type: 'error',
    })

    errorModal.setIsOpen(!!bundlesError)
  }, [bundlesError])

  return {
    isDisabled,
    isOpen,
    setIsOpen,
    tariffName,
    formattedTariffPrice,
    canAddBundle,
    selectedBundles,
    cancelBundlesSet,
    bundlesAndTariffPrice,
  }
}
